<script lang="ts">
  import { page } from '@inertiajs/svelte'
  import { inertia } from '../utils/inertia'
  import { faBars, faCheck } from '@fortawesome/free-solid-svg-icons'
  import Icon from './Icon.svelte'
  import Drawer from './Drawer.svelte'
  import { getI18n } from '../i18n'
  import { languageLinks } from '../languages'
  import type { NavigationLink } from '../types'

  let { links }: { links: NavigationLink[] } = $props()

  const i18n = getI18n()
  const homeHref = $derived(i18n.path('/welcome'))

  let menuOpen = $state(false)
</script>

<Drawer bind:open={menuOpen} closeLabel={i18n.t('navigation.closeMenu')}>
  {#snippet children({ close })}
    <ul class="navigation-drawer-list">
      {#each links as { label, href, icon } (href)}
        <li class="navigation-drawer-item">
          <a {href} {@attach inertia({ href })} class="navigation-drawer-link" onclick={close}>
            <Icon {icon} />
            {label}
          </a>
        </li>
      {/each}
    </ul>
    <div class="navigation-language">
      <p class="navigation-language-label">{i18n.t('navigation.language')}</p>
      <ul class="navigation-language-list">
        {#each languageLinks(page.url, i18n.locale) as { code, flag, name, href, current } (code)}
          <li class="navigation-language-item">
            <a
              {href}
              {@attach inertia({ href })}
              class={['navigation-language-link', { '-current': current }]}
              aria-current={current ? 'true' : undefined}
              onclick={close}
            >
              <span class="navigation-language-flag">{flag}</span>
              <span class="navigation-language-name" lang={code}>{name}</span>
              {#if current}
                <span class="navigation-language-check"><Icon icon={faCheck} /></span>
              {/if}
            </a>
          </li>
        {/each}
      </ul>
    </div>
  {/snippet}
</Drawer>

<nav class="navigation-mobile">
  <ul class="navigation-mobile-list">
    <li class="navigation-mobile-item">
      <a href={homeHref} {@attach inertia({ href: homeHref })} class="navigation-mobile-link">Visit Augsburg</a>
    </li>
    <li class="navigation-mobile-item -end">
      <button class="navigation-mobile-link" aria-label={i18n.t('navigation.openMenu')} aria-haspopup="dialog" aria-expanded={menuOpen} onclick={() => (menuOpen = true)}>
        <Icon icon={faBars} />
      </button>
    </li>
  </ul>
</nav>

<style>
  .navigation-mobile {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    justify-content: space-between;
    height: var(--nav-height);
    width: 100%;
    background-color: var(--color-variant-brand);
    color: var(--color-white);
    border-radius: var(--radius-6);

    @media (--tablet) {
      display: none;
    }
  }

  .navigation-mobile-list {
    display: flex;
    flex-direction: row;
    gap: var(--space-4);
    align-items: stretch;
    list-style: none;
    margin: 0 var(--space-2);
    padding: 0;
    width: 100%;
  }

  .navigation-mobile-item {
    display: flex;
    align-items: stretch;

    &.-end {
      margin-inline-start: auto;
    }
  }

  .navigation-mobile-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    white-space: nowrap;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-white);
    padding: 0 var(--space-7);
    margin: var(--space-2) 0;
    border-radius: var(--radius-6);
  }

  .navigation-drawer-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .navigation-drawer-link {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    text-decoration: none;
    white-space: nowrap;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text);
    padding: var(--space-4) var(--space-5);
    border-radius: var(--radius-2);

    &:hover {
      background-color: var(--color-variant-brand-muted);
    }
  }

  .navigation-language {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    margin-block-start: var(--space-5);
    padding-block-start: var(--space-7);
    border-block-start: var(--border-style-1);
  }

  .navigation-language-label {
    margin: 0;
    padding-inline: var(--space-5);
    font-size: var(--font-size-2);
    font-weight: var(--font-weight-3);
    letter-spacing: var(--letter-spacing-4);
    text-transform: uppercase;
    color: var(--color-variant-danger);
  }

  .navigation-language-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--space-2);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .navigation-language-item {
    display: flex;
    min-width: 0;
  }

  .navigation-language-link {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    width: 100%;
    min-height: var(--space-11);
    padding-inline: var(--space-5);
    border-radius: var(--radius-2);
    text-decoration: none;
    color: var(--color-variant-danger);

    &:hover,
    &.-current {
      background-color: var(--color-variant-danger-muted);
    }

    &.-current {
      font-weight: var(--font-weight-3);
    }
  }

  .navigation-language-flag {
    flex: none;
  }

  .navigation-language-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .navigation-language-check {
    flex: none;
    display: flex;
  }
</style>
